import React, { useState, useEffect } from 'react';
import { Form, Input, Button, notification, Tabs, Card, Space, Collapse, Select, Row, Col, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;

const ContentManage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState({});


  useEffect(() => {
    const fetchCurrentContent = async () => {
      try {
        const response = await fetch('/api/termsconditions');
        const data = await response.json();
        //console.log('Fetched data:', data);
        const formValues = {
          termsTitle: data.terms?.title || '',
          termsContent: data.terms?.content || '',
          privacyTitle: data.privacy?.title || '',
          privacyContent: data.privacy?.content || '',
          faqTitle: data.faq?.title || 'Frequently Asked Questions',
          faqItems: data.faq?.items || [],
          // Contact Us fields
          contactTitle: data.contact?.title || 'Contact Us',
          contactDescription: data.contact?.description || '',
          contactEmail: data.contact?.email || '',
          contactEmail2: data.contact?.email2 || '',
          contactPhone1: data.contact?.phone1 || '',
          contactPhone2: data.contact?.phone2 || '',
          contactAddress: data.contact?.address || '',
          contactMapUrl: data.contact?.mapUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.8839582476147!2d76.2743!3d9.9313!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zOcKwNTUnNTIuNyJOIDc2wrAxNicyNy41IkU!5e0!3m2!1sen!2sin!4v1234567890',
          marqueeText: data.marquee?.marqueeText,
          isActive: data.marquee?.isActive || false,
          backgroundColor: data.marquee?.backgroundColor || '#000000',
          textColor: data.marquee?.textColor || '#FFFFFF'
        };

        form.setFieldsValue(formValues);
        setInitialValues(formValues);
      } catch (error) {
        notification.error({ message: 'Failed to load content' });
      }
    };
    fetchCurrentContent();
  }, [form]);



  const handleSave = async (values) => {
    setLoading(true);
    try {
      let payload;

      if (activeTab === '3') {
        // FAQ data structure
        payload = {
          type: 'faq',
          title: values.faqTitle,
          items: values.faqItems || []
        };
      } else if (activeTab === '4') {
        // Contact Us data structure
        payload = {
          type: 'contact',
          title: values.contactTitle,
          description: values.contactDescription,
          email: values.contactEmail,
          email2: values.contactEmail2,
          phone1: values.contactPhone1,
          phone2: values.contactPhone2,
          address: values.contactAddress,
          mapUrl: values.contactMapUrl
        };
      } else if (activeTab === '5') {
        // Marquee data structure
        payload = {
          type: 'marquee',
          title: 'Marquee',
          marqueeText: values.marqueeText,
          isActive: values.isActive,
          backgroundColor: values.backgroundColor,
          textColor: values.textColor
        };
      } else {
        // Terms & Privacy data structure
        payload = {
          type: activeTab === '1' ? 'terms' : 'privacy',
          title: activeTab === '1' ? values.termsTitle : values.privacyTitle,
          contentdata: activeTab === '1' ? values.termsContent : values.privacyContent
        };
      }

      //console.log('Payload to be sent:', payload);

      const response = await fetch('/api/updateTerms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const successMessage =
          activeTab === '1' ? 'Terms & Conditions' :
            activeTab === '2' ? 'Privacy Policy' :
              activeTab === '3' ? 'FAQ' :
                activeTab === '4' ? 'Contact Us' :
                  'Marquee'; // Added Marquee case

        notification.success({
          message: `${successMessage} updated successfully`
        });
        setIsEditing(false);

        // Update initial values after successful save
        const currentValues = form.getFieldsValue();
        setInitialValues(currentValues);
      }
    } catch (error) {
      notification.error({ message: 'Update failed' });
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    // Reset to initial values
    form.setFieldsValue(initialValues);
    setIsEditing(false);
  };

  const handleEdit = (e) => {
    e?.preventDefault();
    setIsEditing(true);
  };

  const addFaqItem = () => {
    const faqItems = form.getFieldValue('faqItems') || [];
    form.setFieldsValue({
      faqItems: [...faqItems, { question: '', answer: '', category: 'general' }]
    });
  };

  const removeFaqItem = (index) => {
    const faqItems = form.getFieldValue('faqItems') || [];
    const newFaqItems = faqItems.filter((_, i) => i !== index);
    form.setFieldsValue({ faqItems: newFaqItems });
  };

  // React Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'image',
    'blockquote', 'code-block',
    'align'
  ];

  const faqCategories = [
    { value: 'general', label: 'General' },
    { value: 'account', label: 'Account' },
    { value: 'billing', label: 'Billing' },
    { value: 'technical', label: 'Technical' },
    { value: 'privacy', label: 'Privacy' },
    { value: 'terms', label: 'Terms' }
  ];

  return (
    <Card title="Content Management" style={{ margin: '20px' }}>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setIsEditing(false); // Reset editing state when switching tabs
        }}
        type="card"
      >
        {/* Terms & Conditions Tab */}
        <TabPane tab="Terms & Conditions" key="1">
          <Form form={form} onFinish={handleSave} layout="vertical">
            <Form.Item
              name="termsTitle"
              label="Title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input
                placeholder="Enter title for Terms & Conditions"
                disabled={!isEditing}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="termsContent"
              label="Content"
              rules={[{ required: true, message: 'Please enter content' }]}
            >
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter your terms and conditions content..."
                readOnly={!isEditing}
                style={{
                  height: '400px',
                  marginBottom: '50px',
                  backgroundColor: isEditing ? '#fff' : '#f5f5f5'
                }}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                {!isEditing ? (
                  <Button type="primary" onClick={handleEdit} htmlType="button">
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update
                    </Button>
                    <Button onClick={handleCancel} htmlType="button">
                      Cancel
                    </Button>
                  </>
                )}
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        {/* Privacy Policy Tab */}
        <TabPane tab="Privacy Policy" key="2">
          <Form form={form} onFinish={handleSave} layout="vertical">
            <Form.Item
              name="privacyTitle"
              label="Title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input
                placeholder="Enter title for Privacy Policy"
                disabled={!isEditing}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="privacyContent"
              label="Content"
              rules={[{ required: true, message: 'Please enter content' }]}
            >
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter your privacy policy content..."
                readOnly={!isEditing}
                style={{
                  height: '400px',
                  marginBottom: '50px',
                  backgroundColor: isEditing ? '#fff' : '#f5f5f5'
                }}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                {!isEditing ? (
                  <Button type="primary" onClick={handleEdit} htmlType="button">
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update
                    </Button>
                    <Button onClick={handleCancel} htmlType="button">
                      Cancel
                    </Button>
                  </>
                )}
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        {/* FAQ Tab */}
        <TabPane tab="FAQ" key="3">
          <Form form={form} onFinish={handleSave} layout="vertical">
            <Form.Item
              name="faqTitle"
              label="FAQ Section Title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input
                placeholder="Enter FAQ section title"
                disabled={!isEditing}
                size="large"
              />
            </Form.Item>

            <Form.Item label="FAQ Items">
              <Form.List name="faqItems">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        style={{ marginBottom: 16 }}
                        title={`Question ${index + 1}`}
                        extra={
                          isEditing && (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              htmlType="button"
                            >
                              Remove
                            </Button>
                          )
                        }
                      >
                        {/* <Form.Item
                          {...field}
                          name={[field.name, 'category']}
                          label="Category"
                          rules={[{ required: true, message: 'Please select category' }]}
                        >
                          <Select
                            placeholder="Select category"
                            disabled={!isEditing}
                            options={faqCategories}
                          />
                        </Form.Item> */}

                        <Form.Item
                          {...field}
                          name={[field.name, 'question']}
                          label="Question"
                          rules={[{ required: true, message: 'Please enter question' }]}
                        >
                          <Input.TextArea
                            placeholder="Enter question"
                            disabled={!isEditing}
                            rows={2}
                          />
                        </Form.Item>

                        <Form.Item
                          {...field}
                          name={[field.name, 'answer']}
                          label="Answer"
                          rules={[{ required: true, message: 'Please enter answer' }]}
                        >
                          <ReactQuill
                            theme="snow"
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Enter answer..."
                            readOnly={!isEditing}
                            style={{
                              height: '200px',
                              marginBottom: '20px',
                              backgroundColor: isEditing ? '#fff' : '#f5f5f5'
                            }}
                          />
                        </Form.Item>
                      </Card>
                    ))}

                    {isEditing && (
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                        style={{ marginBottom: 16 }}
                        htmlType="button"
                      >
                        Add FAQ Item
                      </Button>
                    )}
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item>
              <Space>
                {!isEditing ? (
                  <Button type="primary" onClick={handleEdit} htmlType="button">
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update FAQ
                    </Button>
                    <Button onClick={handleCancel} htmlType="button">
                      Cancel
                    </Button>
                  </>
                )}
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane tab="Contact Us" key="4">
          <Form form={form} onFinish={handleSave} layout="vertical">
            {/* Contact Section Title */}
            <Form.Item
              name="contactTitle"
              label="Contact Page Title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input
                placeholder="Enter contact page title"
                disabled={!isEditing}
                size="large"
              />
            </Form.Item>

            {/* Contact Description */}

            {/* Contact Information */}
            <Card title="Contact Information" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="contactEmail"
                    label="Email Address"
                    rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                  >
                    <Input
                      placeholder="contact@example.com"
                      disabled={!isEditing}
                      prefix={<MailOutlined />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="contactEmail2"
                    label="Secondary Email Address"
                    rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                  >
                    <Input
                      placeholder="contact@example.com"
                      disabled={!isEditing}
                      prefix={<MailOutlined />}
                    />
                  </Form.Item>
                </Col>

              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="contactPhone1"
                    label="Primary Phone"
                  >
                    <Input
                      placeholder="+91 8888888888"
                      disabled={!isEditing}
                      prefix={<PhoneOutlined />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="contactPhone2"
                    label="Secondary Phone (Optional)"
                  >
                    <Input
                      placeholder="+91 5555555555"
                      disabled={!isEditing}
                      prefix={<PhoneOutlined />}
                    />
                  </Form.Item>
                </Col>

              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="contactAddress"
                    label="Address"
                  >
                    <Input.TextArea
                      placeholder="Enter full address"
                      disabled={!isEditing}
                      rows={2}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="contactMapUrl"
                label="Google Maps Embed URL"
                extra="Paste the embed URL from Google Maps"
              >
                <Input.TextArea
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  disabled={!isEditing}
                  rows={3}
                />
              </Form.Item>
            </Card>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                {!isEditing ? (
                  <Button type="primary" onClick={handleEdit} htmlType="button">
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update Contact Info
                    </Button>
                    <Button onClick={handleCancel} htmlType="button">
                      Cancel
                    </Button>
                  </>
                )}
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        {/* Marquee Tab */}
        <TabPane tab="Marquee" key="5">
          <Form form={form} onFinish={handleSave} layout="vertical">
            {/* Marquee Settings */}
            <Card title="Marquee Settings" style={{ marginBottom: 16 }}>
              <Form.Item
                name="marqueeText"
                label="Marquee Text"
                rules={[{ required: true, message: 'Please enter marquee text' }]}
              >
                <Input.TextArea
                  placeholder="Enter scrolling text for marquee..."
                  disabled={!isEditing}
                  rows={3}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="backgroundColor"
                    label="Background Color"
                  >
                    <Input
                      type="color"
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="textColor"
                    label="Text Color"
                  >
                    <Input
                      type="color"
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="isActive"
                label="Status"
                valuePropName="checked"
              >
                <Switch
                  disabled={!isEditing}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Form.Item>
            </Card>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                {!isEditing ? (
                  <Button type="primary" onClick={handleEdit} htmlType="button">
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update Marquee
                    </Button>
                    <Button onClick={handleCancel} htmlType="button">
                      Cancel
                    </Button>
                  </>
                )}
              </Space>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default ContentManage;